import { Component, OnInit, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { Merchant } from '../../../_models/fundraising/Merchant';
import { DialogService } from '../../../_services/dialog.service';
import { MerchantService } from '../../../_services/fundraising/merchant.service';
import { PhotoService } from '../../../_services/photo.service';

@Component({
  selector: 'app-merchant-create',
  templateUrl: './merchant-create.component.html',
  styleUrls: ['./merchant-create.component.css']
})
export class MerchantCreateComponent implements OnInit {

  public form: FormGroup;
  public merchantId: number;

  public hasValidationErrors: boolean;

  constructor(
    private merchantService: MerchantService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private spinner: NgxSpinnerService,
    private dialogService: DialogService,
    private viewRef: ViewContainerRef,
    private imageService: PhotoService
  ) { }

  ngOnInit(): void {

    this.spinner.show();

    this.route.params
      .pipe(mergeMap(params => {
        this.merchantId = params['id'];
        return this.merchantId > 0 ? this.merchantService.getById(this.merchantId) : of(null);
      })).subscribe(
        data => {
          this.createForm(data);
          this.spinner.hide();
        },
        error => {
          this.dialogService.alert("Unable to load merchant", error.error, this.viewRef);
          this.spinner.hide();
        }
      ); 
  }

  createForm(merchant: Merchant) {
    this.form = this.fb.group({
      merchantId: [merchant?.merchantId],
      name: [merchant?.name, Validators.required],
      active: [merchant?.active],
      email: [{value: merchant?.email, disabled: merchant?.active}, [Validators.required, Validators.email]],
      imageUrl: [merchant?.imageUrl || ''],
      description: [merchant?.description || '', Validators.maxLength(300)],
      homePageUrl: [merchant?.homePageUrl || '', Validators.pattern('(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?')],
      showAddressFields: [false],
      addressLine1: [merchant?.address?.addressLine1],
      addressLine2: [merchant?.address?.addressLine2],
      buildingName: [merchant?.address?.buildingName],
      city: [merchant?.address?.city],
      stateProvince: [merchant?.address?.stateProvince],
      country: [merchant?.address?.country],
      postalCode: [merchant?.address?.postalCode]
    });
  }

  uploadImage(file: File, correctDimensions: boolean){
    if (file && file.type.includes('image') && correctDimensions){
      this.imageService.enqueueUploadPhoto(file, 'charity-image', { height: 406, width: 1380 }, false, true)
        .subscribe(res => {
          this.form.patchValue({ imageUrl: res.url });
        })
    } else {
      this.form.controls.imageUrl.setErrors({ incorrectDimensions : true});
      this.form.controls.imageUrl.markAsTouched();
    }
  }

  verifyImageDimensions (file: File) {

    this.imageService.meetsImageConstraints(file, 1380, 406)
      .subscribe(
        res => {

          this.uploadImage(file, res);
        }
      );
  }

  save() {
    this.hasValidationErrors = !this.form.valid;
    if (this.hasValidationErrors) {
      this.form.markAllAsTouched();
      this.form.markAsDirty();
      return;
    }

    this.spinner.show();

    this.imageService
      .commitUploads()
      .pipe(mergeMap(res => {
        const merchantData = this.form.getRawValue();
        if (this.merchantId > 0) {
          return this.merchantService.update(merchantData);
        } else {
          return this.merchantService.create(merchantData);
        }
      }))
      .subscribe(() => {
        this.spinner.hide();
        this.navigateUp();
      },
      error => {
        this.spinner.hide();
        this.dialogService.alert("Unable to update merchant", error.error, this.viewRef);
      })
  }

  public navigateUp() {
    this.router.navigate([this.merchantId > 0 ? '../../' : '../'], {relativeTo: this.route});
  }

}
